/*
 * ATTENTION!
 *
 * If you just want to track some events, please use `ga` from `helpers/multitrack`
 */

import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import cookie from 'cookie';
import { ANALYTICS_GA_ENABLED } from 'constants/analytics';
import { dashFeatureById } from 'redux/saga/entities/bound';
import { readLocalStorage } from 'redux/saga/helpers/local-storage';

const GA_INIT = `(function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){
(i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),
m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)
})(window,document,'script','//www.google-analytics.com/analytics.js','ga');`;

let gaCheckTimer;
let featureFlagIsLoaded = false;
let gaEnabled = false;
let gaCalls = [];

export const dimensions = {
  EXPERIMENT_BRANCH: 'dimension4',
  USER_TYPE: 'dimension8',
  EXPERIMENT: 'dimension9',
  PROMOTION: 'dimension16',
  USER_ID: 'dimension17',
};

export const getExperimentBranch = (testId, variantId) => (testId ? `test_${testId}_variant_${variantId}` : null);

const nativeGA = () => (__CLIENT__ || __TEST__) && featureFlagIsLoaded && gaEnabled && window.ga;

// Function to check if feature flag is true and to call all GA tracking events if it's available.
const gaCheck = () => {
  const gaFn = nativeGA();
  if (gaFn) {
    clearInterval(gaCheckTimer);
    gaCalls.map(args => gaFn(...args));
    gaCalls = [];
  }
};

// This lives outside of the component so it can be used in places that are
// not in the component hierarchy (such as sagas).
const safeGa = function ga(...args) {
  // If it's a Server we disregard the call as it will called again when the client remounts
  // or if the feature flag returns false, we do a noop since the event won't be sent.
  if ((__SERVER__ && !__TEST__) || (featureFlagIsLoaded && !gaEnabled)) {
    // Clear the interval if we are SSR or if the flag is false;
    if (gaCheckTimer) {
      clearInterval(gaCheckTimer);
    }
    return null;
  }

  const gaFn = nativeGA();
  if (gaFn) {
    if (__CLIENT__ && readLocalStorage('trackingDebug')) {
      console.info('Tracking ga', ...args); // eslint-disable-line no-console
    }
    gaFn(...args);
  } else {
    gaCalls.push(args);
    if (!gaCheckTimer && __CLIENT__) {
      gaCheckTimer = setInterval(gaCheck, 1000);
    }
  }
};

export class GoogleAnalyticsUnwrapped extends React.Component {
  static sendPageView = (path, customDimensions) => {
    safeGa('send', {
      page: path,
      hitType: 'pageview',
      ...customDimensions,
    });
  };

  static setIsFlagLoaded = value => {
    featureFlagIsLoaded = value;
  };

  static setGaEnabled = value => {
    gaEnabled = value;
  };

  componentDidMount() {
    const { enableGa, flagLoaded } = this.props;

    GoogleAnalyticsUnwrapped.setIsFlagLoaded(flagLoaded);
    GoogleAnalyticsUnwrapped.setGaEnabled(enableGa);

    const cookies = cookie.parse(document.cookie);
    safeGa('set', dimensions.USER_TYPE, cookies.cpUserType || 'prospect');
    safeGa('send', 'pageview');
  }

  componentDidUpdate(prevProps) {
    const { currentPath, userId, flagLoaded, enableGa } = this.props;

    if (flagLoaded !== prevProps.flagLoaded) {
      GoogleAnalyticsUnwrapped.setIsFlagLoaded(flagLoaded);
    }
    if (enableGa !== prevProps.enableGa) {
      GoogleAnalyticsUnwrapped.setGaEnabled(enableGa);
    }
    // Track a GA page view if the URL changes
    if (currentPath !== prevProps.currentPath) {
      safeGa('send', 'pageview');
    }
    if (!prevProps.userId && userId) {
      safeGa('set', dimensions.USER_ID, String(userId));
    }
  }

  render() {
    const { accountKey, enableGa, cookieDomain, userId } = this.props;

    if (!enableGa) {
      return null;
    }

    const initLines = [];

    initLines.push(`ga('create', '${accountKey}', { cookieDomain: '${cookieDomain}' });`);

    // Optimizely Universal Analytics Integration
    initLines.push('window.optimizely = window.optimizely || [];');
    initLines.push('window.optimizely.push("activateUniversalAnalytics");');

    if (userId) {
      initLines.push(`ga('set', 'userId', '${userId}');`);
      initLines.push(`ga('set', '${dimensions.USER_ID}', '${userId}');`);
    }

    return (
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: `${GA_INIT}\n${initLines.join('\n')}`.replace(/\n/g, '') }}
      />
    );
  }
}

GoogleAnalyticsUnwrapped.propTypes = {
  currentPath: PropTypes.string.isRequired,
  accountKey: PropTypes.string.isRequired,
  cookieDomain: PropTypes.string.isRequired,
  enableGa: PropTypes.bool,
  flagLoaded: PropTypes.bool,
  userId: PropTypes.number,
};

const mapStateToProps = state => ({
  enableGa: dashFeatureById.data(state, ANALYTICS_GA_ENABLED),
  flagLoaded: dashFeatureById.loaded(state, ANALYTICS_GA_ENABLED),
});

export const GoogleAnalytics = connect(mapStateToProps)(GoogleAnalyticsUnwrapped);
